import {AX_ORDER_STATUS, PAYMENT_TYPES} from '../constants/constants';

/**
 * Milliseconds for an Instant as jj-order-svc writes it: an ISO string
 * (WRITE_DATES_AS_TIMESTAMPS is disabled there), with epoch seconds or
 * milliseconds accepted should the numeric form ever reach a client. NaN when
 * there is nothing usable.
 */
export const instantToMs = value => {
  if (typeof value === 'number') {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'string') {
    return Date.parse(value);
  }
  return NaN;
};

// Every status an order can carry without being a placed, paid-for order.
const NOT_CONFIRMED_STATUSES = [
  AX_ORDER_STATUS.PAYMENT_PENDING,
  AX_ORDER_STATUS.PAYMENT_PENDING_EXPIRED,
  AX_ORDER_STATUS.PAYMENT_AUTH_REQUIRED,
  AX_ORDER_STATUS.PAYMENT_VOIDED,
  AX_ORDER_STATUS.CANCELLED,
  AX_ORDER_STATUS.SLOT_EXPIRED,
  AX_ORDER_STATUS.EDIT_EXPIRED,
  AX_ORDER_STATUS.TOTAL_DISCREPANCY,
];

/**
 * A draft (no status yet) or a Pay By Bank order held for the bank: the
 * checkout is still in flight and nothing has been placed.
 */
export const isOrderAwaitingPayment = order =>
  !!order &&
  (!order.status || order.status === AX_ORDER_STATUS.PAYMENT_PENDING);

/**
 * A Pay By Bank order the customer has come back to from their bank, but that
 * has no status yet: no notification from the provider has reached us. It is
 * a payment in flight, not a failure, however long the provider takes.
 */
export const isPayByBankAwaitingBank = order =>
  !!order && !order.status && order.paymentMethod === PAYMENT_TYPES.PBB.mode;

/**
 * Placed and paid for (or on account): the only state that counts as a
 * confirmed purchase. SUBMITTED, CREATED, APPROVED and later statuses all
 * qualify; anything in NOT_CONFIRMED_STATUSES does not.
 */
export const isOrderConfirmed = order =>
  !!order &&
  !!order.status &&
  NOT_CONFIRMED_STATUSES.indexOf(order.status) === -1;

/**
 * Whether a fetched copy of the order in state is older than that state and
 * must not replace it: a slow poll response landing after the notifier's
 * refetch has already moved the status on. Without comparable timestamps the
 * fetched copy is taken as current.
 */
export const isStaleOrderUpdate = (fetched, current) => {
  if (!fetched || !current || fetched.orderId !== current.orderId) {
    return false;
  }
  const fetchedMs = instantToMs(fetched.statusChangedAt);
  const currentMs = instantToMs(current.statusChangedAt);
  if (!Number.isFinite(fetchedMs) || !Number.isFinite(currentMs)) {
    return false;
  }
  return fetchedMs < currentMs;
};
